import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { DEFAULT_TDM_DATA } from '../../constants/tdm';
import type { typeTopicsData } from '../../constants/typeDefinitions/crTypes';
import type { SnackbarConfig } from '../../utility/services/customHooks/snackbar.types';
import integrationsApi from '../services/api/integrations';
import schemaApi from '../services/api/schema';
import sessionApi from '../services/api/session';
import commonApi from '../services/api/common';
import { reducer } from './reducers';

export interface ContentRecommendation {
  projectedContentQualityScore: string;
  finalurl: string;
}
export const initialState = {
  lookerUserId: undefined,
  sidebarOpen: true,
  topTrendsList: [] as any[],
  showFullScreen: false,
  explores: [] as any[],
  looksList: [] as any[],
  dashboardsList: [] as any[] as any[],
  blobUrls: {},
  looker_permissions: [] as any[],
  snackConfig: {
    show: false,
    open: false,
    type: 'error' as const,
    text: 'Oops! Something Went Wrong. Please try again..',
  } as SnackbarConfig,
  adHocAnalysisFolders: [] as any[],
  segmentFiltersData: {},
  loadersArray: [] as string[],
  competitorsData: {},
  drillDownRowData: {},
  topNData: [] as any[],
  gtSelectedFilters: {
    tempSegmentData: {
      segmentData_1: {},
      segmentData_2: {},
    },
  },
  stSelectedFilters: {
    tempSegmentData: {
      segmentData_1: {},
      segmentData_2: {},
    },
  },
  headerInfo: {
    title: '',
  },
  potentialOpportunityList: [] as any[],
  topOpportunityList: [] as any[],
  growthTrendsMarketShareFilters: {},
  datePickerFilter: {} as any,
  segment1_name: 'Primary Segment',
  segment2_name: 'Comparison Segment',
  usabilityRecommendationList: [] as any[],
  contentRecommendationList: [] as ContentRecommendation[],
  contentBriefList: [] as any[],
  customerPagesUploads: [] as any[],
  customerKeywordsUploads: [] as any[],
  slackChannels: [] as any[],
  customerInfo: [] as any[],
  userSelectedDateRange: {},
  defaultDateRange: {},
  summaryMetricData: {},
  drilldownCachedMetricData: {},
  recommendationFiltersData: {},
  contentRecommendationFiltersData: {} as Record<string, any>,
  usabilityScore: [] as any[],
  defaultDomain: '',
  defaultMarketSegment: undefined,
  confirmationModal: {},
  crDetailsModal: {},
  addPageToOptimizeModelForTrends: {},
  intentList: [] as any[],
  ruleList: [] as any[],
  entityList: [] as any[],
  ruleStoreList: [] as any[],
  intentAllSearchList: [] as any[],
  intentSummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
  },
  intentAllSearchSummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
  },
  categoryList: [] as any[],
  categorySummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
    no_of_pages: 0,
  },
  categoryAllSearchList: [] as any[],
  categoryAllSearchSummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
    no_of_pages: 0,
  },
  categoryPageList: [] as any[],
  // customerPreviewHtml: {},
  showCrFullScreenSpinner: false, // BEFORE CALL NEW ACTION, HOLD THE USER TO UPDATE ALL THE SCORES AFTER THE RESPONSE of ADD/UPDATE/DELETE ACTION
  ////-------------CR RELATED STATES-------
  crPageTagsHtmlDataCollection: {}, // like h1:[{all h1 data of page}], h2:[{all h2 data of page}], p:[]
  keywordsData: [] as any[], // all Targeted keywords
  cikKeywordsData: [] as any[],
  pageAllHtmlData: [] as any[],
  allCompetitorsHtmlData: [] as any[],
  topicsData: {
    ...DEFAULT_TDM_DATA,
  } as unknown as typeTopicsData,
  potentialKeywordsData: [] as any[],
  aiKeywordsData: [] as any[],
  combinedKeywordsData: [] as any[],
  isTriggerIncludedPotentialKeyword: false,
  masterLanguageAndCountry: {},
  globalAutoWriterMagicPin: {
    open: false,
    defaultText: '',
  },
  contentBriefTinyMceData: {
    initialLoad: true,
    contentBriefString: '',
  },
  gptStreamingData: {
    text: '',
    isHtmlMarkup: false,
  },
  gptCompleteContent: {
    content: '',
    timestamp: 0,
  },
  gptThinkingState: {
    isThinking: false,
    status: '',
    message: '',
  },
  gptThinkingContent: '',
  gptPersistedThinkingContent: {
    content: '',
    timestamp: 0,
    promptInfo: {
      promptFor: '',
      keyword: '',
      title: '',
    },
  },
  wsInstance: null as any,
  elementMappingDetails: undefined as any | undefined,
  elementMappingDetailsCompetitor: undefined as any | undefined,
  showSubscriberBanner: false as boolean | string,
  subscriberMeta: null as any,
  creditsRemaining: 0 as any,
  dayPassCreditsRemaining: 0 as any,
  creditsUsagePerDay: 0 as any,
  customPopupConfig: {
    showPopup: false,
    text: '',
    anchorText: '',
    anchorLink: '',
  },
  autoNomousInternalLinkingDetails: {
    status: '',
    list: [],
  },
  usersList: [] as any[],
};

export const initializeStore = (preloadedState?: Partial<InitialStateType>) => {
  const store = configureStore({
    reducer: {
      // Add the generated reducer as a specific top-level slice
      [sessionApi.reducerPath]: sessionApi.reducer,
      [integrationsApi.reducerPath]: integrationsApi.reducer,
      [schemaApi.reducerPath]: schemaApi.reducer,
      [commonApi.reducerPath]: commonApi.reducer,
      root: reducer,
    },
    preloadedState: { [sessionApi.reducerPath]: undefined, root: { ...initialState, ...preloadedState } },
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      })
        .concat(sessionApi.middleware)
        .concat(integrationsApi.middleware)
        .concat(schemaApi.middleware)
        .concat(commonApi.middleware),
  });

  setupListeners(store.dispatch);

  return store;
};

export const APP_STORE = initializeStore();

export type InitialStateType = typeof initialState;
export type InitialStoreType = ReturnType<typeof initializeStore>;
